import { memo } from "react";
import { animated, to, useSpring } from "@react-spring/web";
import { Handle, Position, type NodeProps } from "@xyflow/react";
import type { HealthMapFlowNode } from "./healthMapUtils";

export const HealthMapNode = memo(function HealthMapNode({ data, selected }: NodeProps<HealthMapFlowNode>) {
  const node = data.healthNode;
  const motion = data.motion;
  const base = data.baseScale ?? 1;
  const opacity = data.opacity ?? 1;
  const isSphere = data.sphereMode === true;

  const depth = selected ? Math.max(motion.depth, 18) : motion.depth;
  const spring = useSpring({
    config: motion.active
      ? { tension: 640, friction: 24, mass: 0.68 }
      : { tension: 320, friction: 14, mass: 0.82 },
    depth,
    immediate: false,
    rotate: isSphere ? 0 : motion.rotate,
    rotateX: isSphere ? 0 : (selected && !motion.active ? motion.rotateX - 1.8 : motion.rotateX),
    rotateY: isSphere ? 0 : motion.rotateY,
    scale: selected ? base * 1.06 : motion.active ? base * 1.06 : motion.neighbor ? base * 1.025 : base,
    x: isSphere ? 0 : motion.offsetX,
    y: isSphere ? 0 : motion.offsetY
  });

  if (isSphere) {
    return (
      <animated.div
        className={[
          "health-flow-node sphere-node",
          `node-${data.statusTone}`,
          data.roleClass,
          selected ? "selected" : ""
        ].filter(Boolean).join(" ")}
        style={{
          opacity,
          transform: to(
            [spring.scale],
            (scale) => `scale(${scale})`
          )
        }}
      >
        <Handle className="health-flow-handle" position={Position.Top} type="target" />
        <Handle className="health-flow-handle" position={Position.Bottom} type="source" />
        <Handle className="health-flow-handle" position={Position.Left} type="target" />
        <Handle className="health-flow-handle" position={Position.Right} type="source" />
        <span className="sphere-node-dot" aria-hidden="true" />
        <span className="sphere-node-label">{node.label}</span>
      </animated.div>
    );
  }

  return (
    <animated.div
      className={[
        "health-flow-node",
        `node-${data.statusTone}`,
        data.roleClass,
        selected ? "selected" : "",
        motion.active ? "dragging" : "",
        motion.neighbor ? "motion-neighbor" : "",
        motion.settling ? "settling" : ""
      ].filter(Boolean).join(" ")}
      style={{
        transform: to(
          [spring.x, spring.y, spring.depth, spring.rotateX, spring.rotateY, spring.rotate, spring.scale],
          (x, y, z, rotateX, rotateY, rotate, scale) =>
            `translate3d(${x}px, ${y}px, ${z}px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) rotate(${rotate}deg) scale(${scale})`
        )
      }}
    >
      <Handle className="health-flow-handle" position={Position.Top} type="target" />
      <Handle className="health-flow-handle" position={Position.Bottom} type="source" />
      <Handle className="health-flow-handle" position={Position.Left} type="target" />
      <Handle className="health-flow-handle" position={Position.Right} type="source" />

      <div className="health-flow-node-header">
        <span className="health-flow-node-dot" aria-hidden="true" />
        <span>{data.statusLabel}</span>
      </div>

      {data.showLabels ? (
        <div className="health-flow-node-body">
          <strong>{node.label}</strong>
          <span>{node.sourceType || node.kind}</span>
          <small>{data.subtitle}</small>
        </div>
      ) : (
        <span className="health-flow-node-compact-label">{node.label}</span>
      )}

      <div className="health-flow-tooltip" role="tooltip">
        <strong>{node.label}</strong>
        <span>{data.subtitle}</span>
        <p>{node.detail}</p>
      </div>
    </animated.div>
  );
});
